/**
 * Browser-side image pyramid. The server never has to decode a huge file:
 * we produce the variants here and upload them next to the original. They are WebP where the
 * browser encodes it; WebKit (Safari, and every browser on an iPad or iPhone) cannot, and gets
 * JPEG for opaque pictures and PNG where transparency has to survive.
 */

export const VARIANT_SIZES = { thumb: 256, medium: 1024, large: 2560 } as const;
export type VariantName = keyof typeof VARIANT_SIZES | 'original';
export const VARIANT_ORDER: VariantName[] = ['thumb', 'medium', 'large', 'original'];

export interface VariantBlob {
  blob: Blob;
  w: number;
  h: number;
}

export interface GeneratedVariants {
  width: number;
  height: number;
  variants: Partial<Record<Exclude<VariantName, 'original'>, VariantBlob>>;
  /** Re-encoded original (possibly downscaled) when compression produced something worth keeping or the type had to change. */
  original?: VariantBlob;
}

/** Original types the server stores as they are (Assets.php ORIGINAL_MIMES); others are re-encoded. */
const STORED_TYPES = new Set(['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'image/avif', 'image/bmp']);
/** Types without transparency: their fallback encoding may be JPEG. */
const OPAQUE_TYPES = new Set(['image/jpeg', 'image/heic', 'image/heif']);
/** Quality for an original that has to be re-encoded although the device asked for no compression. */
const CONVERT_QUALITY = 0.92;

export interface CompressOptions {
  /** Re-encode the original as lossy WebP. */
  webp: boolean;
  /** 0..1 */
  quality: number;
  /** Longest side to keep, in pixels; 0 = no limit. */
  maxSide: number;
}

/** Smallest variant whose longest side covers `neededPx`, else the original. */
export function pickVariant(neededPx: number): VariantName {
  if (neededPx <= VARIANT_SIZES.thumb) return 'thumb';
  if (neededPx <= VARIANT_SIZES.medium) return 'medium';
  if (neededPx <= VARIANT_SIZES.large) return 'large';
  return 'original';
}

export async function generateVariants(file: Blob, compress?: CompressOptions): Promise<GeneratedVariants> {
  const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  try {
    let width = bitmap.width;
    let height = bitmap.height;
    let source: ImageBitmap | HTMLCanvasElement = bitmap;
    const out: GeneratedVariants = { width, height, variants: {} };
    const opaque = OPAQUE_TYPES.has(file.type);

    // Replace the stored original when it has to change: downscaled to the board's cap, or a type
    // the server does not store (HEIC from an iPad's photo library). Otherwise only lossy WebP is
    // worth it, where the browser encodes WebP. Animated GIFs keep their animation only as the
    // original file, so compression leaves them alone.
    const compressing = !!compress?.webp && file.type !== 'image/gif';
    const unstored = !STORED_TYPES.has(file.type);
    const cap = compressing && compress.maxSide > 0 ? compress.maxSide : Infinity;
    const capped = Math.max(width, height) > cap;
    if (unstored || capped || (compressing && (await canEncodeWebp()))) {
      if (capped) {
        const s = cap / Math.max(width, height);
        width = Math.max(1, Math.round(bitmap.width * s));
        height = Math.max(1, Math.round(bitmap.height * s));
        source = downscale(bitmap, width, height);
      }
      const canvas = source instanceof HTMLCanvasElement ? source : draw(source, width, height);
      const blob = await encodePicture(canvas, opaque, compressing ? compress.quality : CONVERT_QUALITY);
      // a WebP that is not smaller than an already-compressed original is not worth keeping
      if (unstored || capped || blob.size < file.size) {
        out.original = { blob, w: width, h: height };
        out.width = width;
        out.height = height;
        source = canvas;
      } else {
        source = bitmap;
      }
    }
    const longest = Math.max(width, height);

    // Generate from largest to smallest, each step downscaling the previous result for quality.
    for (const name of ['large', 'medium', 'thumb'] as const) {
      const max = VARIANT_SIZES[name];
      if (longest <= max) continue; // the original already is small enough for this level
      const scale = max / longest;
      const w = Math.max(1, Math.round(width * scale));
      const h = Math.max(1, Math.round(height * scale));
      const canvas = downscale(source, w, h);
      const blob = await encodePicture(canvas, opaque, 0.86);
      out.variants[name] = { blob, w, h };
      source = canvas;
    }
    return out;
  } finally {
    bitmap.close();
  }
}

/**
 * Variant pyramid of a canvas that already holds the final pixels (a painted sketch-sheet layer).
 * The caller keeps the canvas's own lossless encoding as the original.
 */
export async function variantsFromCanvas(canvas: HTMLCanvasElement): Promise<GeneratedVariants> {
  const width = canvas.width;
  const height = canvas.height;
  const out: GeneratedVariants = { width, height, variants: {} };
  const longest = Math.max(width, height);
  let source: HTMLCanvasElement = canvas;
  for (const name of ['large', 'medium', 'thumb'] as const) {
    const max = VARIANT_SIZES[name];
    if (longest <= max) continue;
    const scale = max / longest;
    const w = Math.max(1, Math.round(width * scale));
    const h = Math.max(1, Math.round(height * scale));
    const c = downscale(source, w, h);
    const blob = await encodePicture(c, false, 0.86);
    out.variants[name] = { blob, w, h };
    source = c;
  }
  return out;
}

/** Stepwise halving before the final resample avoids aliasing on big downscales. */
function downscale(source: ImageBitmap | HTMLCanvasElement, w: number, h: number): HTMLCanvasElement {
  let cur: ImageBitmap | HTMLCanvasElement = source;
  let cw = source.width;
  let ch = source.height;
  while (cw / 2 >= w && ch / 2 >= h) {
    cw = Math.floor(cw / 2);
    ch = Math.floor(ch / 2);
    cur = draw(cur, cw, ch);
  }
  return cur.width === w && cur.height === h && cur instanceof HTMLCanvasElement ? cur : draw(cur, w, h);
}

function draw(source: ImageBitmap | HTMLCanvasElement, w: number, h: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2D canvas unavailable');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(source, 0, 0, w, h);
  return canvas;
}

let webpProbe: Promise<boolean> | null = null;

/** Whether this browser's canvas encodes WebP (WebKit hands back a PNG for any type it lacks). */
export function canEncodeWebp(): Promise<boolean> {
  webpProbe ??= (async () => {
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = 1;
    try {
      return (await canvasToBlob(canvas, 'image/webp', 0.8)).type === 'image/webp';
    } catch {
      return false;
    }
  })();
  return webpProbe;
}

/** WebP where the browser encodes it, else JPEG for an opaque picture and PNG where alpha matters. */
async function encodePicture(canvas: HTMLCanvasElement, opaque: boolean, quality: number): Promise<Blob> {
  if (await canEncodeWebp()) return canvasToBlob(canvas, 'image/webp', quality);
  return canvasToBlob(canvas, opaque ? 'image/jpeg' : 'image/png', quality);
}

/** File extension for an encoded picture's type. */
export function extForType(type: string): string {
  return type === 'image/jpeg' ? 'jpg' : type === 'image/png' ? 'png' : 'webp';
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('toBlob failed'))), type, quality);
  });
}
